import { Component, inject, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SalesViewService, STORAGE_KEY_VIEW } from '../sales-view.service';
import { ApiService } from 'src/app/components/shared/api.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { localStorageService } from 'src/app/components/shared/localstorage.service';
import { PermissionsService } from 'src/app/components/shared/permissions/permissions.service';

@Component({
    selector: 'app-sales-header',
    imports: [CommonModule, FormsModule],
    templateUrl: './sales-header.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './sales-header.component.scss'
})
export class SalesHeaderComponent implements OnInit, OnDestroy {

  constructor(@Inject(SalesViewService) public salesView: SalesViewService) { }

  private _api = inject(ApiService);
  private destroy$ = new Subject<void>();
  private localStorageService = inject(localStorageService);
  private permissions = inject(PermissionsService);
  isTeamLead = false;
  isManager = false;

  /**
   * Sub-flag: only L1 Team Leads should see the per-agent dropdown when
   * `view === 'team'`. We can't tell L1-TL from L2-TL via capabilities alone
   * (both have VIEW_TEAM); the cheapest signal is `dashboards.l1` AND not
   * `dashboards.l2` AND not manager.
   */
  isL1TeamLead = false;

  /** Active tab keys driven by manager vs non-manager mode. */
  primaryTab: 'L1' | 'my' = 'my';
  secondaryTab: 'L2' | 'team' = 'team';
  primaryLabel = 'My view';
  secondaryLabel = 'Team view';

  teamList: any;
  profileData: any;

  private fetchTeamList(): void {
    this._api.api({}, 'sales/team', 'get')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.teamList = res?.team ?? [];
          // L1 TL sees themselves in the team selector, manager doesn't.
          if (this.isL1TeamLead) {
            const current_user = {
              admin_id: this.profileData?.admin_id,
              admin_name: this.profileData?.admin_name,
              role_id: this.profileData?.role_id,
              admin_email: this.profileData?.admin_email
            };
            this.teamList.push(current_user);
          }
        },
        error: () => { this.teamList = []; }
      });
  }

  ngOnInit() {
    this.profileData = this.localStorageService.customGetItem(STORAGE_KEY_VIEW.PROFILE);

    this.permissions.snapshot$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refreshFromCapabilities());

    this.salesView.isTeamLead$
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => this.isTeamLead = v);
    this.salesView.isManager$
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => this.isManager = v);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshFromCapabilities(): void {
    const flags = this.permissions.salesPermissionFlags;
    const isManagerNow = flags.isManager;
    const isTeamLeadNow = flags.isTeamLead;
    this.isL1TeamLead = flags.isTeamLeadL1;

    if (isManagerNow) {
      this.primaryTab = 'L1';
      this.secondaryTab = 'L2';
      this.primaryLabel = 'Team L1';
      this.secondaryLabel = 'Team L2';
    } else {
      this.primaryTab = 'my';
      this.secondaryTab = 'team';
      this.primaryLabel = 'My view';
      this.secondaryLabel = 'Team view';
    }

    if ((isTeamLeadNow || isManagerNow) && !this.teamList) {
      this.fetchTeamList();
    }
  }

  setView(view: 'my' | 'team' | 'L1' | 'L2'): void {
    this.salesView.setDashboardView(view);
  }

  onAdminChange(adminId: number | null): void {
    this.salesView.setSelectedAdminId(adminId);
  }
}
